import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Checkbox,
  Text,
  Box,
  Divider,
  useToast,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from "@chakra-ui/react";
import { useState, useEffect, useMemo } from "react";

const AutoReminderSettingsModal = ({
  isOpen,
  onClose,
  reminderDaysBeforeAppointment,
  setReminderDaysBeforeAppointment,
  reminderScheduledTime,
  setReminderScheduledTime,
  reminderPatientTypeFilter,
  setReminderPatientTypeFilter,
  patients,
  onApplySettings,
  scheduleReminderOneDayBefore,
  setScheduleReminderOneDayBefore,
}) => {
  const toast = useToast();

  // Settings state
  const [daysBeforeAppointment, setDaysBeforeAppointment] = useState(1);
  const [scheduledTime, setScheduledTime] = useState("09:00");
  const [includeEmail, setIncludeEmail] = useState(true);
  const [includePhone, setIncludePhone] = useState(true);
  const [localPatientTypeFilter, setLocalPatientTypeFilter] = useState([]);
  const [scheduleReminders, setScheduleReminders] = useState(false);

  // Predefined patient types to always include in filter
  const predefinedPatientTypes = ["NP", "NS"];

  // Get available patient types from patients, restricted to NP and NS only.
  const availablePatientTypes = useMemo(() => {
    const types = new Set(predefinedPatientTypes);
    patients.forEach((patient) => {
      if (patient.type) {
        const normalized = patient.type.toUpperCase().trim();
        if (predefinedPatientTypes.includes(normalized)) {
          types.add(normalized);
        }
      }
    });
    return Array.from(types).sort();
  }, [patients]);

  // Initialize settings when modal opens
  useEffect(() => {
    if (isOpen) {
      setDaysBeforeAppointment(reminderDaysBeforeAppointment || 1);
      setScheduledTime(reminderScheduledTime || "09:00");
      setIncludeEmail(true);
      setIncludePhone(true);
      // Strip any non-NP/NS values so stale DB settings (e.g. ["NP","NS","FFP","FFS"])
      // no longer show those types as selected.
      const sanitize = (arr) =>
        (arr || []).filter((t) => predefinedPatientTypes.includes(t.toUpperCase().trim()));
      if (reminderPatientTypeFilter && reminderPatientTypeFilter.length > 0) {
        setLocalPatientTypeFilter(sanitize(reminderPatientTypeFilter));
      } else {
        // Default: select all available patient types
        setLocalPatientTypeFilter(sanitize(availablePatientTypes));
      }
      setScheduleReminders(scheduleReminderOneDayBefore || false);
    }
  }, [
    isOpen,
    reminderDaysBeforeAppointment,
    reminderScheduledTime,
    reminderPatientTypeFilter,
    scheduleReminderOneDayBefore,
    availablePatientTypes,
  ]);

  // Get patient count for filter
  const getFilteredPatientCount = (filter) => {
    if (filter.length === 0) return patients.length;
    return patients.filter(
      (p) =>
        p.type && localPatientTypeFilter.includes(p.type.toUpperCase().trim()),
    ).length;
  };

  const handleApplySettings = () => {
    // Strip any non-NP/NS values before persisting so stale FFP/FFS entries
    // saved in older sessions do not resurface.
    const sanitizedFilter = localPatientTypeFilter.filter((t) =>
      predefinedPatientTypes.includes(t.toUpperCase().trim()),
    );
    onApplySettings({
      daysBeforeAppointment,
      scheduledTime,
      includeEmail,
      includePhone,
      patientTypeFilter: sanitizedFilter,
      scheduleReminders,
    });
    toast({
      title: "Settings Applied",
      description: `Auto reminder settings have been updated.`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
    onClose();
  };

  const handlePatientTypeChange = (type, isChecked) => {
    if (isChecked) {
      setLocalPatientTypeFilter((prev) => [...prev, type]);
    } else {
      setLocalPatientTypeFilter((prev) => prev.filter((t) => t !== type));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Auto Reminder Settings</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={5} align="stretch">
            {/* Days Before Appointment Settings */}
            <Box>
              <Text fontWeight="bold" mb={3}>
                Reminder Timing
              </Text>
              <VStack spacing={3} align="stretch">
                <HStack spacing={3}>
                  <FormControl>
                    <FormLabel fontSize="sm">Days Before</FormLabel>
                    <NumberInput
                      size="sm"
                      min={0}
                      max={365}
                      value={daysBeforeAppointment}
                      onChange={(_, value) =>
                        setDaysBeforeAppointment(value || 1)
                      }
                      w="100px"
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Time (PST)</FormLabel>
                    <Input
                      type="time"
                      size="sm"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      w="130px"
                    />
                  </FormControl>
                </HStack>
                <Text fontSize="xs" color="gray.600">
                  Reminders will be sent{" "}
                  {daysBeforeAppointment === 0
                    ? "on the same day"
                    : daysBeforeAppointment === 1
                      ? "1 day before"
                      : `${daysBeforeAppointment} days before`}{" "}
                  at {scheduledTime} PST
                </Text>
              </VStack>
            </Box>

            <Divider />

            {/* Scheduling Setting */}
            <Box>
              <Text fontWeight="bold" mb={3}>
                Scheduling
              </Text>
              <VStack align="flex-start" spacing={2}>
                <Checkbox
                  isChecked={scheduleReminders}
                  onChange={(e) => setScheduleReminders(e.target.checked)}
                >
                  <Text fontSize="sm">
                    Schedule reminders instead of sending immediately
                  </Text>
                </Checkbox>
                <Text fontSize="xs" color="gray.600">
                  When enabled, reminders are queued now and sent at the
                  scheduled day and time (default: {scheduledTime} PST{" "}
                  {daysBeforeAppointment === 0
                    ? "on the same day"
                    : daysBeforeAppointment === 1
                      ? "1 day before"
                      : `${daysBeforeAppointment} days before`}
                  ).
                </Text>
              </VStack>
            </Box>

            <Divider />

            {/* Contact Method Settings */}
            <Box>
              <Text fontWeight="bold" mb={3}>
                Contact Methods
              </Text>
              <VStack align="flex-start" spacing={2}>
                <Checkbox
                  isChecked={includeEmail}
                  onChange={(e) => setIncludeEmail(e.target.checked)}
                >
                  <Text fontSize="sm">Send via Email</Text>
                </Checkbox>
                <Checkbox
                  isChecked={includePhone}
                  onChange={(e) => setIncludePhone(e.target.checked)}
                >
                  <Text fontSize="sm">Send via SMS/Phone</Text>
                </Checkbox>
              </VStack>
            </Box>

            <Divider />

            {/* Patient Type Filter */}
            <Box>
              <HStack justify="space-between" mb={3}>
                <Text fontWeight="bold">Patient Types</Text>
                {localPatientTypeFilter.length !==
                  availablePatientTypes.length && (
                  <Button
                    size="xs"
                    variant="ghost"
                    onClick={() =>
                      setLocalPatientTypeFilter([...availablePatientTypes])
                    }
                  >
                    Select All
                  </Button>
                )}
                {localPatientTypeFilter.length ===
                  availablePatientTypes.length &&
                  localPatientTypeFilter.length > 0 && (
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => setLocalPatientTypeFilter([])}
                    >
                      Clear All
                    </Button>
                  )}
              </HStack>
              <VStack
                align="flex-start"
                spacing={2}
                border="1px"
                borderColor="gray.200"
                borderRadius="md"
                p={3}
                bg="gray.50"
                maxHeight="200px"
                overflowY="auto"
              >
                {availablePatientTypes.length === 0 ? (
                  <Text fontSize="sm" color="gray.500">
                    No patient types available
                  </Text>
                ) : (
                  availablePatientTypes.map((type) => (
                    <Checkbox
                      key={type}
                      isChecked={localPatientTypeFilter.includes(type)}
                      onChange={(e) =>
                        handlePatientTypeChange(type, e.target.checked)
                      }
                    >
                      <Text fontSize="sm">{type}</Text>
                    </Checkbox>
                  ))
                )}
              </VStack>
              <Text fontSize="xs" color="gray.500" mt={2}>
                {getFilteredPatientCount(localPatientTypeFilter)} of{" "}
                {patients.length} patients selected
              </Text>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="green" onClick={handleApplySettings}>
            Apply Settings
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AutoReminderSettingsModal;
